import { type ReactNode } from 'react';
import {
  Button,
  IconButton,
  IconClose,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { Dialog } from '@tamagui/dialog';

import { buildTestId } from '~/utils/buildTestId';

import { ModalContent } from './ModalContent';
import { ModalOverlay } from './ModalOverlay';

type ModalAction = {
  label: string;
  onPress: () => void;
  disabled?: boolean;
};

type ModalProps = {
  /** Test ID for testing purposes */
  testID: string;
  /** Modal Title */
  title: string;
  /** Modal Body */
  children: ReactNode;
  /** Sets the modal width */
  size?: 'm' | 'l';
  /**
   * The primary action of the modal.
   *
   * If `onPress` is not provided, the modal will be closed when the primary action is pressed.
   */
  primaryAction?: Omit<ModalAction, 'onPress'> &
    Partial<Pick<ModalAction, 'onPress'>>;
  /** Optional secondary action of the modal */
  secondaryAction?: ModalAction;
  /** Action at the right side of the header */
  titleAction?: ModalAction;
  /**
   * On Open change callback
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Flag indicating if the modal should have a close button
   */
  hasCloseButton?: boolean;
};

const modalWidths = {
  m: 800,
  l: 1100,
};

export const Modal = ({
  title,
  children,
  size = 'm',
  primaryAction,
  secondaryAction,
  titleAction,
  onOpenChange,
  hasCloseButton = false,
  testID,
}: ModalProps) => {
  const handleClose = () => {
    onOpenChange?.(false);
  };

  const isThereActionButtons =
    primaryAction !== undefined || secondaryAction !== undefined;

  return (
    <Dialog open onOpenChange={onOpenChange} modal>
      <Dialog.Portal
        testID={buildTestId({
          testID,
          suffix: 'portal',
        })}
      >
        <ModalOverlay
          testID={buildTestId({
            testID,
            suffix: 'overlay',
          })}
        />

        <ModalContent
          testID={testID}
          key="content"
          gap="$2"
          maxWidth={modalWidths[size]}
          backgroundColor="$neutral.0"
        >
          <Stack flexDirection="row" gap="$4">
            <Dialog.Title unstyled flex={1}>
              <Text
                type="$h3.medium"
                color="$text.100"
                testID={buildTestId({ testID, suffix: 'title' })}
              >
                {title}
              </Text>
            </Dialog.Title>

            {titleAction && (
              <Button
                variant="primary"
                size="small"
                testID={buildTestId({
                  testID,
                  suffix: ['title', 'action'],
                })}
                {...titleAction}
              />
            )}
            {hasCloseButton && (
              <IconButton
                aria-label="Close modal"
                onPress={handleClose}
                size="S"
                circle
                variant="secondary"
                // @ts-expect-error Parallax doesn't type IconButton borderWidth
                borderWidth={0}
                testID={buildTestId({ testID, suffix: ['close', 'button'] })}
              >
                <IconClose />
              </IconButton>
            )}
          </Stack>

          {children}

          {isThereActionButtons && (
            <Stack
              flexDirection="row"
              justifyContent="flex-end"
              gap="$4"
              paddingTop="$5"
              paddingRight="$2"
              paddingBottom="$2"
              testID={buildTestId({
                testID,
                suffix: 'actions',
              })}
            >
              {secondaryAction && (
                <Button
                  variant="secondary"
                  {...secondaryAction}
                  // @ts-expect-error Parallax doesn't type Button minWidth
                  minWidth={180}
                  testID={buildTestId({
                    testID,
                    suffix: ['secondary', 'action'],
                  })}
                />
              )}
              {primaryAction && (
                <Button
                  variant="primary"
                  onPress={handleClose}
                  {...primaryAction}
                  // @ts-expect-error Parallax doesn't type Button minWidth
                  minWidth={180}
                  testID={buildTestId({
                    testID,
                    suffix: ['primary', 'action'],
                  })}
                />
              )}
            </Stack>
          )}
        </ModalContent>
      </Dialog.Portal>
    </Dialog>
  );
};
